// v10/src/hooks/useOfflineQueue.js
import { useEffect, useRef, useCallback } from 'react';
import { inspectionService } from '../services/apiService';
import {
  enqueueInspection,
  getPendingInspections,
  updateQueueEntry,
  setConfig,
} from '../utils/idbQueue';

const MAX_RETRIES = 3;

// ── Guardar token en IndexedDB para que el SW pueda usarlo ────────
// Se llama desde App.js al hacer login
export const syncTokenToIDB = async (token, apiUrl) => {
  try {
    await setConfig('auth_token', token);
    await setConfig('api_url', apiUrl || process.env.REACT_APP_API_URL || 'https://condor360-backend.onrender.com');
  } catch (e) {
    console.warn('[OfflineQueue] No se pudo sincronizar token a IDB:', e);
  }
};

// ── Hook principal ────────────────────────────────────────────────
export const useOfflineQueue = () => {
  const isFlushing = useRef(false);

  // Registrar Background Sync en el Service Worker
  const registerBackgroundSync = useCallback(async () => {
    if (!('serviceWorker' in navigator) || !('SyncManager' in window)) return;
    try {
      const registration = await navigator.serviceWorker.ready;
      await registration.sync.register('sync-inspections');
      console.info('[OfflineQueue] Background Sync registrado');
    } catch (e) {
      console.warn('[OfflineQueue] Background Sync no disponible:', e.message);
    }
  }, []);

  // Agregar inspección a la cola IndexedDB
  const enqueue = useCallback(async (inspectionData) => {
    try {
      const entry = await enqueueInspection(inspectionData);
      console.info(`[OfflineQueue] Inspección encolada en IDB: ${entry.id}`);

      // Registrar sync tag para que el SW lo intente en cuanto haya conexión
      await registerBackgroundSync();

      return entry.id;
    } catch (e) {
      console.error('[OfflineQueue] Error al encolar:', e);
      return null;
    }
  }, [registerBackgroundSync]);

  // Intentar enviar todas las entradas pendientes (se llama desde la app)
  const flush = useCallback(async () => {
    if (isFlushing.current || !navigator.onLine) return;

    const pending = await getPendingInspections();
    const toRetry = pending.filter(e => e.retryCount < MAX_RETRIES);
    if (toRetry.length === 0) return;

    isFlushing.current = true;
    console.info(`[OfflineQueue] Sincronizando ${toRetry.length} inspección(es)...`);

    for (const entry of toRetry) {
      try {
        const response = await inspectionService.create({
          ...entry.payload,
          offline_queue_id: entry.id,
        });

        if (response.data.success) {
          await updateQueueEntry(entry.id, {
            status:   'synced',
            syncedAt: new Date().toISOString(),
          });
          console.info(`[OfflineQueue] ✅ Sincronizada: ${entry.id}`);

          window.dispatchEvent(new CustomEvent('condor360:offline-synced', {
            detail: { queueId: entry.id, inspection: response.data.data }
          }));
        }
      } catch (err) {
        const newCount = (entry.retryCount || 0) + 1;
        await updateQueueEntry(entry.id, {
          retryCount: newCount,
          lastError:  err.message,
          status:     newCount >= MAX_RETRIES ? 'failed' : 'pending',
        });
        console.warn(`[OfflineQueue] ⚠️ Falló reintento ${newCount}/${MAX_RETRIES} para ${entry.id}`);
      }
    }

    isFlushing.current = false;
  }, []);

  // Escuchar evento 'online' para reintentar desde la app
  useEffect(() => {
    const handleOnline = () => {
      console.info('[OfflineQueue] Conexión restaurada — sincronizando...');
      flush();
    };
    window.addEventListener('online', handleOnline);
    if (navigator.onLine) flush();
    return () => window.removeEventListener('online', handleOnline);
  }, [flush]);

  return { enqueue, flush };
};

export const getOfflinePendingCount = async () => {
  try {
    const pending = await getPendingInspections();
    return pending.length;
  } catch { return 0; }
};
